<template>
  <div :class="$style.report_table">
    <div :class="$style.table_header">
      <div
        v-for="header in tableHeaders"
        :key="header.id"
        :class="$style.header_cell"
      >
        <header-cell
          :cell="header"
          :width="`${columnSizing[header.column.id]}px`"
          :skin="skin"
          :sort-direction="header.column.getIsSorted()"
          :is-first-column="header.index === 0"
          :is-last-column="getIsLastHeaderColumn(header)"
          :is-can-sort="header.column.getCanSort()"
          @click="(e) => toggleSorting(e, header)"
        />
        <div
          v-if="header.column.getCanResize() && !getIsLastHeaderColumn(header)"
          :class="$style.resizer"
          @mousedown.stop="e => header.getResizeHandler()(e)"
        />
      </div>
    </div>

    <div
      v-if="isShowStub"
      :class="$style.stub"
    >
      <div :class="$style.container">
        <svgImage
          :class="$style.img"
          :name="'stub-box'"
        />
        <div :class="$style.text">
          {{ locales(`report_page_no_filtered_data`) }}
        </div>
      </div>
    </div>

    <div
      ref="totalRowRef"
      v-if="!isShowStub"
      :class="$style.total_row"
      :style="{height: totalRowItems.length ? '36px' : '16px'}"
    >
      <div
        v-if="totalRowItems.length "
        :class="$style.wrapper"
      >
        <div
          v-for="(cell, index) in totalRowItems"
          :key="index"
          :class="$style.cell"
          :style="[
            {width: `${cell.size}px`},
            cell.size === 0 ? {display: 'none'} : {}
          ]"
        >
          <div
            v-if="cell.show && cell.size > 64"
            :class="$style.label"
          >
            {{ locales('common_total') }}
          </div>
          <div
            v-if="cell.show"
            :class="{
              [$style.value]: true,
              [$style.is_negative]: cell.isNegativeValue
            }"
          >
            {{ cell.value }}
          </div>
        </div>
      </div>
    </div>

    <div
      ref="scrollerRef"
      :style="{
        height: `${scrollContainerHeight}px`,
        overflow: 'auto',
      }"
      :class="$style.scroll_container"
    >
      <div
        :class="$style.table_body"
        :style="{height: `${totalSize}px`}"
      >
        <div
          v-for="row in virtualRows"
          :key="row.index"
          :class="$style.table_row"
          :style="getRowPositionStyle(row)"
        >
          <div
            :class="$style.inner_wrapper"
            :style="getRowVisualStyle(row)"
          >
            <div
              v-for="cell in tableRows[row.index].getVisibleCells()"
              :id="cell.id"
              :key="cell.id"
              :style="[
                { width: `${rowsColumnSizing[cell.column.id]}px` },
                getGroupedCellStyle(row, cell),
              ]"
            >
              <button
                v-if="cell.getIsGrouped()"
                @click="tableRows[row.index].getToggleExpandedHandler()()"
              >
                <table-cell
                  :is-row-expanded="tableRows[row.index].getIsExpanded()"
                  :row="tableRows[row.index]"
                  :cell="cell"
                  :columns-sizes="rowsColumnSizing"
                  :skin="skin"
                  :is-last-column="getIsLastColumn(cell)"
                />
              </button>
              <table-cell
                v-else
                :row="tableRows[row.index]"
                :cell="cell"
                :columns-sizes="rowsColumnSizing"
                :skin="skin"
                :is-last-column="getIsLastColumn(cell)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TableController from './controller';
import headerCell from './headerCell.vue';
import tableCell from './cell.vue';
import svgImage from '../../../common/VueComponents/VueIcon/vgpSvgImage.vue';

function getScrollbarWidth() {
  const div = document.createElement("div");

  div.style.position = "absolute";
  div.style.top = "-9999px";
  div.style.width = "100px";
  div.style.height = "100px";
  div.style.overflow = "scroll";

  document.body.appendChild(div);

  const scrollbarWidth = div.offsetWidth - div.clientWidth;

  div.remove();

  return scrollbarWidth;
}

export default {
  name: 'ReportTable',
  components: { headerCell, tableCell, svgImage },
  props: {
    config: { type: Object, required: true },
    rowsData: { type: Array, required: true, default: () => [] },
    grouping: { type: Array, required: true },
    expanded: { type: [Object, Boolean], required: false, default: () => {} },
    expandSettings: { type: Object, required: false, default: () => {} },
    skin: { type: String, required: false, default: '' },
    localStorageTableId: { type: String, required: false, default: '' },
  },
  emits: {
    onSortingChange: null,
    saveExpanded: Object || Boolean,
  },
  data() {
    return {
      locales: __,
      tableController: null,
      columnSizing: {},
      tableHeaders: [],
      tableRows: [],
      virtualRows: [],
      totalSize: 0,
      totalValues: {},
      totalRowItems: [],
      scrollContainerHeight: 0,
      prevContainerWidth: 0,
      resizeObserver: null,
      resizeTimeoutId: null,
      isShowStub: false,
    };
  },
  computed: {
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    rowsColumnSizing() {
      const visibleHeaders = this.tableController.tableInstance?.getHeaderGroups()[0].headers;
      const firstColumnId = visibleHeaders?.[0]?.id;
      const lastColumnId = visibleHeaders?.[visibleHeaders.length - 1]?.id;
      const tableSidePadding = 16;
      const tableRowBorderWidth = 1;

      const rowsColumnSizing = Object.fromEntries(
        Object.entries(this.columnSizing).map(([id, size]) => {
          return [
            id,
            id === firstColumnId || id === lastColumnId
              ? size - (tableSidePadding + tableRowBorderWidth)
              : size,
          ];
        }),
      );

      return rowsColumnSizing;
    }
  },
  watch: {
    rowsData: {
      handler(newRowsData) {
        if (this.tableController?.tableInstance) {
          this.tableController.isUpdating = true;
          this.tableController.updateData(newRowsData);
          this.$nextTick(() => {
            this.initTotalRow();
          });
        }
        this.isShowStub = !newRowsData?.length;
      },
      deep: true,
      immediate: true,
    },
    grouping: {
      handler(newGrouping) {
        if (this.tableController?.tableInstance) {
          this.tableController.isUpdating = true;
          this.tableController.updateGrouping(newGrouping);
          this.$nextTick(() => {
            this.initTotalRow();
          });
        }
      },
      deep: true,
    },
    'config.state.columnVisibility': {
      handler(newVisibility) {
        if (this.tableController?.tableInstance) {
          this.tableController.isUpdating = true;
          this.tableController.updateVisibleColumns(newVisibility, this.config);
          this.columnSizing = this.tableController.controllerColumnSizing;
          this.$nextTick(() => {
            this.initTotalRow();
          });
        }
      },
      deep: true,
      immediate: true,
    },
    'expandSettings.key': {
      handler() {
        if (this.tableController?.tableInstance) {
          // this.tableController?.toggleAllExpanded(this.expandSettings?.isAllExpanded);
          this.tableController?.toggleTopLevelExpanded(this.expandSettings?.isAllExpanded);
        }
      },
      deep: true,
      immediate: true,
    },
    columnSizing: {
      handler(newSizing) {
        if (this.totalRowItems.length) {
          this.initTotalRow();
        }
        localStorage.setItem(this.localStorageTableId, JSON.stringify(newSizing));
      },
      deep: true,
    },
  },
  created() {
    this.tableController = new TableController();
  },
  beforeUnmount() {
    const scrollerElement = this.$refs.scrollerRef;
    const tableState = this.tableController.tableInstance.getState();

    this.$emit('saveExpanded', tableState.expanded);

    window.removeEventListener('resize', this.calcScrollContainerHeight);

    if (scrollerElement) {
      scrollerElement.removeEventListener('scroll', this.onScroll);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.tableController = null;
  },
  mounted() {
    this.$nextTick(() => {
      const scrollbarWidth = getScrollbarWidth();
      document.documentElement.style.setProperty('--reports-table-scrollbar-width', `${scrollbarWidth}px`);

      this.init();
    })
  },
  methods: {
    init() {
      this.initTable();
      this.initVirtualizer();
      this.initResizeObserver();
    },
    initTable() {
      const expanded = this.expanded ? this.expanded : true;

      this.tableController.initTable(
        {
          data: {
            columns: this.config.columns,
            data: this.rowsData,
          },
          state: {
            ...this.config.state,
            grouping: this.grouping,
            expanded: {},
          },
          storageId: this.localStorageTableId,
          updateColumnSizingVue: this.updateColumnSizing,
        },
      );

      this.tableController?.tableInstance.setOptions(prev => ({
        ...prev,
        state: {
          ...prev.state,
          expanded,
        },
      }));

      this.columnSizing = this.tableController.controllerColumnSizing;
      this.initTotalRow();
    },

    initVirtualizer() {
      this.calcScrollContainerHeight();
      this.$nextTick(() => {
        const scrollerElement = this.$refs.scrollerRef;

        this.tableController.initVirtualizer(scrollerElement, this.updateTable);
        this.addEventListeners();
      });
    },

    initTotalRow() {
      this.totalRowItems = [];
      const row = this.tableController.createTableTotalRow(this.config, this.rowsColumnSizing, this.$route);

      this.totalRowItems = row;
    },

    initResizeObserver() {
      const el = this.$el;
      const rect = el.getBoundingClientRect();

      this.prevContainerWidth = rect.width;

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.resizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const { width } = entry.contentRect;
          const stepDiff = width - this.prevContainerWidth;

          if (stepDiff !== 0) {
            console.log('Table resize:', stepDiff, 'px');

            if (this.resizeTimeoutId) {
              clearTimeout(this.resizeTimeoutId);
            }

            if (Math.abs(stepDiff) === 220) { // если ресайз сайдбара
              this.tableController.updateColumnSizesAfterResizeWindow();
              this.columnSizing = this.tableController.controllerColumnSizing;
              this.prevContainerWidth = width;
            } else {
              this.resizeTimeoutId = setTimeout(() => { // для пересчета колонок при ресайзе окна требуется задержка
                this.tableController.updateColumnSizesAfterResizeWindow();
                this.columnSizing = this.tableController.controllerColumnSizing;
                this.prevContainerWidth = width;
                this.resizeTimeoutId = null;
              }, 0);
            }  
          }
        });
      });

      this.resizeObserver.observe(el);
    },

    calcScrollContainerHeight() {
      this.scrollContainerHeight = document.documentElement.clientHeight - 144;
    },

    getRowPositionStyle(virtualRow) {
      return this.tableController.getRowPositionStyle(virtualRow);
    },

    getRowVisualStyle(virtualRow) {
      return this.tableController.getRowVisualStyle(virtualRow);
    },

    getGroupedCellStyle(virtualRow, cell) {
      return this.tableController.getGroupedCellStyle({
        row: this.tableRows[virtualRow.index],
        cell,
        columnSizes: this.rowsColumnSizing,
        config: this.config,
      });
    },

    updateVirtualizer() {
      if (this.tableController?.virtualizerInstance) {
        this.tableController.updateVirtualizer();
      }
    },

    updateTable() {
      if (!this.tableController) return;

      const modifiedTableHeadersWithCorrectVarianceTooltip = this.tableController.tableHeaders.map(header => {
        const varianceColumn = this.config.columns.find(col => col.accessorKey === 'variance' && col.accessorKey === header.id);

        if (!varianceColumn?.headerIconTooltip) return header;

        return {
          ...header,
          column: {
            ...header.column,
            columnDef: {
              ...header.column.columnDef,
              headerIconTooltip: { ...varianceColumn.headerIconTooltip },
            },
          },
        };
      });

      this.tableHeaders = modifiedTableHeadersWithCorrectVarianceTooltip;
      this.tableRows = this.tableController.tableRows;
      this.virtualRows = this.tableController.virtualRows;
      this.totalSize = this.tableController.virtualizerTotalSize;
    },

    updateColumnSizing() {
      this.columnSizing = this.tableController.controllerColumnSizing;
    },

    addEventListeners() {
      const scrollerElement = this.$refs.scrollerRef;

      window.addEventListener('resize', this.calcScrollContainerHeight);

      if (scrollerElement) {
        scrollerElement.addEventListener('scroll', this.onScroll);
      }
    },

    toggleSorting(e, header) {
      const isCanSort = header.column.getCanSort();

      if (!isCanSort) return;

      this.$emit('onSortingChange');
      header.column.toggleSorting();
    },

    onScroll() {
      // const scrollerElement = this.$refs.scrollerRef;
      // const totalRow = this.$refs.totalRowRef;

      // if (scrollerElement.scrollTop > 0) {
      //   totalRow.style.borderBottomColor = '#E5E5E5';
      // } else {
      //   totalRow.style.borderBottomColor = 'transparent';
      // }
    },

    getIsLastColumn(cell) {
      const allVisibleColumns = this.tableController.tableInstance.getVisibleLeafColumns();
      const lastColumn = allVisibleColumns[allVisibleColumns.length -1];
      return cell?.column?.id === lastColumn?.id
    },

    getIsLastHeaderColumn(header) {
      const visibleHeaders = this.tableController.tableInstance?.getHeaderGroups()[0].headers;
      return header.index === visibleHeaders.length - 1
    }
  },
};
</script>

<style module lang="less">
@import '../../../../less/_mixins.less';
@import '../../../../less/_fonts.less';
.report_table {
  height: 100%;
  background: #F8F8F8;
  .table_header {
    display: flex;
    width: 100%;
    background: #fff;
    border-bottom: 1px solid #E5E5E5;

    .header_cell {
      position: relative;
      .resizer {
        position: absolute;
        right: 0;
        top: 0;
        height: 100%;
        width: 4px;
        background: #1565C0;
        opacity: 0;
        cursor: col-resize;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .total_row {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;

    .wrapper {
      display: flex;
      align-items: center;

      .cell {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 12px;

        .label {
          max-width: 50px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          .FontLatoRegular;
          .fs(10px, 14px);
          color: #808080;
          text-transform: uppercase;
        }

        .value {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          .FontLatoBold;
          .fs(14px, 24px);
          text-align: right;
          color: #191919;

          &.is_negative {
            color: #DD3636;
          }
        }
      }
    }
  }

  .scroll_container {
    scrollbar-gutter: stable;
  }

  .table_body {
    position: relative;

    .table_row {
      position: absolute;
      top: 0;
      left: 16px;
      right: calc(16px - var(--reports-table-scrollbar-width));
      display: flex;
      overflow: hidden;

      .inner_wrapper {
        display: flex;
        background: #fff;
        width: 100%;
      }
    }
  }

  .stub {
    width: 100%;
    height: calc(100vh - 160px);
    display: flex;
    align-items: center;
    justify-content: center;

    .container {
      display: flex;
      flex-direction: column;
      align-items: center;

      .img {
        width: 320px;
        height: 188px;
      }

      .text {
        .FontLatoRegular;
        .fs(14px, 20px);
        color: #808080;
        padding-top: 16px;
      }
    }
  }
}
</style>
